import { useState, useEffect, useMemo } from 'react';
import { Clock, Calendar, User, CheckCircle, AlertTriangle, RefreshCw, Bell, Edit2, Save, X, ChevronUp, ChevronDown, Search, Power, FileX, Download } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

// Termination reason options
const TERMINATION_REASONS = [
  "Abandon de poste",
  "Démission",
  "Faute lourde",
  "Faute grave",
  "Absences multiples",
  "Insuffisance professionnelle",
  "Fin de période d'essai (initiative employeur)",
  "Fin de période d'essai (initiative salarié)"
];

const AdminTrialPeriods = ({ user, activeModule = "b2b" }) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'user_name', direction: 'asc' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployeeStatus, setFilterEmployeeStatus] = useState('all'); // all, active, inactive
  const [alertsCount, setAlertsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal for termination reason
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationTarget, setTerminationTarget] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  
  // Full employee data for export
  const [fullEmployeeData, setFullEmployeeData] = useState([]);

  useEffect(() => {
    fetchTrials();
    fetchAlertsCount();
    fetchFullEmployeeData();
  }, []);

  const fetchTrials = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trial-periods`, { withCredentials: true });
      setTrials(res.data || []);
    } catch (error) {
      console.error('Failed to fetch trial periods:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch full employee data for export (includes phone, address, etc.)
  const fetchFullEmployeeData = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      const employees = (res.data || []).filter(u => u.role === 'employee');
      setFullEmployeeData(employees);
    } catch (error) {
      console.error('Failed to fetch full employee data:', error);
    }
  };

  const fetchAlertsCount = async () => {
    try {
      const res = await axios.get(`${API}/trial-periods/alerts-count`, { withCredentials: true });
      setAlertsCount(res.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch alerts count:', error);
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const res = await axios.post(`${API}/trial-periods/check-alerts`, {}, { withCredentials: true });
      alert(`${res.data.message}`);
      fetchTrials();
      fetchAlertsCount();
    } catch (error) {
      console.error('Failed to check alerts:', error);
      alert('Erreur lors de la vérification des alertes');
    }
  };

  const handleEdit = (trial) => {
    setEditingRow(trial.user_id);
    setEditData({
      start_date: trial.start_date || '',
      extended: trial.extended || false
    });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleSave = async (userId, hasExistingTrial) => {
    if (!editData.start_date) {
      alert('Veuillez saisir une date de début');
      return;
    }

    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      if (hasExistingTrial) {
        // Update existing
        await axios.put(`${API}/trial-periods/${userId}`, {
          start_date: editData.start_date,
          extended: editData.extended
        }, { withCredentials: true });
      } else {
        // Create new
        await axios.post(`${API}/trial-periods`, {
          user_id: userId,
          start_date: editData.start_date,
          extended: editData.extended
        }, { withCredentials: true });
      }
      
      await fetchTrials();
      setEditingRow(null);
      setEditData({});
    } catch (error) {
      console.error('Failed to save trial period:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleExtension = async (userId, currentExtended) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.put(`${API}/trial-periods/${userId}`, {
        extended: !currentExtended
      }, { withCredentials: true });
      await fetchTrials();
    } catch (error) {
      console.error('Failed to toggle extension:', error);
      alert('Erreur lors de la modification');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Toggle employee active status (synced with Employees page)
  // When deactivating, requires termination reason
  const handleToggleActive = async (userId, currentActive, userName) => {
    if (currentActive) {
      // Deactivating - open modal to select reason
      setTerminationTarget({ userId, userName });
      setSelectedReason('');
      setShowTerminationModal(true);
    } else {
      // Reactivating
      const confirmMsg = `Voulez-vous réactiver ${userName} ? Il redeviendra actif.`;
      if (!confirm(confirmMsg)) return;
      
      setSaving(prev => ({ ...prev, [userId]: true }));
      try {
        await axios.put(`${API}/users/${userId}/reactivate`, {}, { withCredentials: true });
        await fetchTrials();
      } catch (error) {
        console.error(`Failed to reactivate employee:`, error);
        alert(`Erreur lors de la réactivation`);
      } finally {
        setSaving(prev => ({ ...prev, [userId]: false }));
      }
    }
  };

  // Confirm deactivation with termination reason
  const handleConfirmDeactivation = async () => {
    if (!terminationTarget || !selectedReason) {
      alert('Veuillez sélectionner une cause de fin de contrat');
      return;
    }
    
    const { userId, userName } = terminationTarget;
    
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.put(`${API}/users/${userId}/deactivate`, {
        termination_reason: selectedReason
      }, { withCredentials: true });
      
      setShowTerminationModal(false);
      setTerminationTarget(null);
      setSelectedReason('');
      await fetchTrials();
    } catch (error) {
      console.error(`Failed to deactivate employee:`, error);
      alert(error.response?.data?.detail || `Erreur lors de la désactivation`);
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Update termination reason for already deactivated employee
  const handleUpdateTerminationReason = async (userId, reason) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.put(`${API}/users/${userId}/termination-reason`, {
        termination_reason: reason
      }, { withCredentials: true });
      await fetchTrials();
    } catch (error) {
      console.error('Failed to update termination reason:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Export employee profiles to Excel
  const exportProfilesToExcel = () => {
    // Merge trial data with full employee data
    const exportData = [];
    
    // Helper to format date
    const formatDateForExcel = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
    // Header row
    exportData.push([
      'Nom', 
      'Prénom', 
      'Email', 
      'Téléphone', 
      'Adresse', 
      'Code Postal', 
      'Ville',
      'Statut',
      'Début Période Essai',
      'Fin Période Essai',
      'Prolongation',
      'Type Contrat',
      'Date Désactivation',
      'Cause de Fin'
    ]);
    
    // Get all employees combining trial and full data
    trials.forEach(trial => {
      // Find full employee data
      const fullData = fullEmployeeData.find(e => e.user_id === trial.user_id) || {};
      
      // Extract first name and last name
      let firstName = fullData.first_name || '';
      let lastName = fullData.last_name || '';
      
      // If not available, try to split the name
      if (!firstName && !lastName && trial.user_name) {
        const nameParts = trial.user_name.split(' ');
        if (nameParts.length >= 2) {
          lastName = nameParts[0];
          firstName = nameParts.slice(1).join(' ');
        } else {
          lastName = trial.user_name;
        }
      }
      
      exportData.push([
        lastName,
        firstName,
        fullData.email || trial.email || '',
        fullData.phone || '',
        fullData.address || '',
        fullData.postal_code || '',
        fullData.city || '',
        trial.active ? 'Actif' : 'Désactivé',
        formatDateForExcel(trial.start_date || fullData.trial_start_date),
        formatDateForExcel(trial.end_date),
        trial.extended ? 'Oui' : 'Non',
        trial.contract_type === 'cdi' ? 'CDI' : 'Période d\'essai',
        formatDateForExcel(trial.deactivation_date || fullData.deactivation_date),
        trial.termination_reason || fullData.termination_reason || ''
      ]);
    });
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Nom
      { wch: 15 }, // Prénom
      { wch: 30 }, // Email
      { wch: 15 }, // Téléphone
      { wch: 35 }, // Adresse
      { wch: 10 }, // Code Postal
      { wch: 15 }, // Ville
      { wch: 12 }, // Statut
      { wch: 14 }, // Début PE
      { wch: 14 }, // Fin PE
      { wch: 12 }, // Prolongation
      { wch: 15 }, // Type Contrat
      { wch: 14 }, // Date Désactivation
      { wch: 35 }, // Cause de Fin
    ];
    
    // Generate filename with date
    const today = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
    const filename = `Profils_Salaries_${today}.xlsx`;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Profils');
    
    // Download file
    XLSX.writeFile(wb, filename);
  };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort data
  const filteredAndSortedTrials = useMemo(() => {
    let result = [...trials];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(t => 
        t.user_name?.toLowerCase().includes(query) ||
        t.email?.toLowerCase().includes(query)
      );
    }
    
    // Filter by employee status (active/inactive)
    if (filterEmployeeStatus === 'active') {
      result = result.filter(t => t.active === true);
    } else if (filterEmployeeStatus === 'inactive') {
      result = result.filter(t => t.active === false);
    }
    
    // Filter by trial period status
    if (filterStatus !== 'all') {
      if (filterStatus === 'in_progress') {
        result = result.filter(t => t.status === 'in_progress');
      } else if (filterStatus === 'ending_soon') {
        result = result.filter(t => t.status === 'ending_soon');
      } else if (filterStatus === 'completed') {
        result = result.filter(t => t.status === 'completed' || t.contract_type === 'cdi');
      } else if (filterStatus === 'not_set') {
        result = result.filter(t => !t.has_trial);
      }
    }
    
    // Sort - always alphabetically by name first, then by selected column
    result.sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      
      if (sortConfig.key === 'end_date' || sortConfig.key === 'start_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [trials, filterStatus, filterEmployeeStatus, sortConfig, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const inProgress = trials.filter(t => t.status === 'in_progress').length;
    const endingSoon = trials.filter(t => t.status === 'ending_soon').length;
    const completed = trials.filter(t => t.status === 'completed' || t.contract_type === 'cdi').length;
    const notSet = trials.filter(t => !t.has_trial).length;
    const activeCount = trials.filter(t => t.active === true).length;
    const inactiveCount = trials.filter(t => t.active === false).length;
    return { inProgress, endingSoon, completed, notSet, total: trials.length, activeCount, inactiveCount };
  }, [trials]);

  const getStatusBadge = (trial) => {
    if (!trial.has_trial) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Non définie</span>;
    }
    if (trial.contract_type === 'cdi' || trial.status === 'completed') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">✓ CDI</span>;
    }
    if (trial.status === 'ending_soon') {
      return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700 font-medium">⚠ Bientôt terminée</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">En période d'essai</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const days = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
      <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] font-['Chivo'] flex items-center gap-3">
              <Clock className="w-8 h-8" />
              Période d'essai
            </h1>
            <p className="text-gray-500 mt-1">Gestion des périodes d'essai des salariés</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={fetchTrials}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={exportProfilesToExcel} className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300">
              <Download className="w-4 h-4 mr-2" />
              Export Profils
            </Button>
            <Button onClick={handleCheckAlerts} className="bg-orange-600 hover:bg-orange-700">
              <Bell className="w-4 h-4 mr-2" />
              Vérifier alertes
              {alertsCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white text-orange-600 rounded-full text-xs font-bold">
                  {alertsCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className={`cursor-pointer transition-all ${filterStatus === 'all' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setFilterStatus('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
              <p className="text-sm text-gray-500">Total salariés</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${filterStatus === 'in_progress' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setFilterStatus('in_progress')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              <p className="text-sm text-gray-500">En période d'essai</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${filterStatus === 'ending_soon' ? 'ring-2 ring-orange-500' : ''}`}
                onClick={() => setFilterStatus('ending_soon')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.endingSoon}</p>
              <p className="text-sm text-gray-500">Bientôt terminée</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${filterStatus === 'completed' ? 'ring-2 ring-green-500' : ''}`}
                onClick={() => setFilterStatus('completed')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-sm text-gray-500">CDI confirmé</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${filterStatus === 'not_set' ? 'ring-2 ring-gray-500' : ''}`}
                onClick={() => setFilterStatus('not_set')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-500">{stats.notSet}</p>
              <p className="text-sm text-gray-500">Non définie</p>
            </CardContent>
          </Card>
        </div>

        {/* Employee Status Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-600">Statut salarié :</span>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterEmployeeStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterEmployeeStatus === 'all' 
                  ? 'bg-[#0F172A] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tous ({stats.total})
            </button>
            <button
              onClick={() => setFilterEmployeeStatus('active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterEmployeeStatus === 'active' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              ● Actifs ({stats.activeCount})
            </button>
            <button
              onClick={() => setFilterEmployeeStatus('inactive')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterEmployeeStatus === 'inactive' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              ○ Anciens / Off ({stats.inactiveCount})
            </button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Règles de la période d'essai :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Durée initiale : <strong>2 mois</strong></li>
                  <li>Prolongation possible : <strong>+2 mois</strong> (une seule fois)</li>
                  <li>À la fin de la période, le salarié passe automatiquement en <strong>CDI</strong></li>
                  <li>Alertes automatiques : 1 mois, 2 semaines, 1 semaine, et jour de fin</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Liste des salariés ({filteredAndSortedTrials.length})
              </CardTitle>
              
              {/* Search Bar */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher un salarié..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="trial-periods-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th 
                      className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('user_name')}
                    >
                      Nom du salarié <SortIcon column="user_name" />
                    </th>
                    <th 
                      className="text-center p-3 font-semibold cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('start_date')}
                    >
                      Date début <SortIcon column="start_date" />
                    </th>
                    <th 
                      className="text-center p-3 font-semibold cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('end_date')}
                    >
                      Date fin <SortIcon column="end_date" />
                    </th>
                    <th className="text-center p-3 font-semibold">Prolongation</th>
                    <th className="text-center p-3 font-semibold">Statut</th>
                    <th className="text-center p-3 font-semibold">Jours restants</th>
                    <th className="text-center p-3 font-semibold min-w-[180px]">Cause de fin</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedTrials.map((trial) => {
                    const isEditing = editingRow === trial.user_id;
                    const isSaving = saving[trial.user_id];
                    const daysRemaining = getDaysRemaining(trial.end_date);
                    
                    return (
                      <tr 
                        key={trial.user_id} 
                        className={`border-b hover:bg-gray-50 ${
                          !trial.active ? 'bg-gray-50 opacity-75' :
                          trial.status === 'ending_soon' ? 'bg-orange-50' : 
                          trial.contract_type === 'cdi' ? 'bg-green-50' : ''
                        }`}
                        data-testid={`trial-row-${trial.user_id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900">{trial.user_name}</div>
                            <button
                              onClick={() => handleToggleActive(trial.user_id, trial.active, trial.user_name)}
                              disabled={isSaving}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium transition-all cursor-pointer hover:opacity-80 ${
                                trial.active 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                              title={trial.active ? 'Cliquer pour désactiver' : 'Cliquer pour réactiver'}
                            >
                              <Power className="w-3 h-3" />
                              {trial.active ? 'Actif' : 'Off'}
                            </button>
                          </div>
                          <div className="text-xs text-gray-500">{trial.email}</div>
                        </td>
                        <td className="p-3 text-center">
                          {isEditing ? (
                            <Input
                              type="date"
                              value={editData.start_date}
                              onChange={(e) => setEditData(prev => ({ ...prev, start_date: e.target.value }))}
                              className="w-36 mx-auto"
                            />
                          ) : (
                            <span className={trial.start_date ? 'text-gray-900' : 'text-gray-400'}>
                              {formatDate(trial.start_date)}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className={trial.end_date ? 'font-medium text-gray-900' : 'text-gray-400'}>
                            {formatDate(trial.end_date)}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {isEditing ? (
                            <label className="flex items-center justify-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editData.extended}
                                onChange={(e) => setEditData(prev => ({ ...prev, extended: e.target.checked }))}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">+2 mois</span>
                            </label>
                          ) : trial.has_trial ? (
                            <button
                              onClick={() => handleToggleExtension(trial.user_id, trial.extended)}
                              disabled={isSaving || trial.contract_type === 'cdi'}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                trial.extended 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              } ${(isSaving || trial.contract_type === 'cdi') ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {trial.extended ? 'Oui (+2 mois)' : 'Non'}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {getStatusBadge(trial)}
                        </td>
                        <td className="p-3 text-center">
                          {trial.has_trial && trial.contract_type !== 'cdi' && daysRemaining !== null ? (
                            <span className={`font-bold ${
                              daysRemaining < 0 ? 'text-red-600' :
                              daysRemaining <= 7 ? 'text-red-600' :
                              daysRemaining <= 30 ? 'text-orange-600' :
                              'text-gray-700'
                            }`}>
                              {daysRemaining < 0 ? `${Math.abs(daysRemaining)} j dépassé` : `${daysRemaining} j`}
                            </span>
                          ) : trial.contract_type === 'cdi' ? (
                            <span className="text-green-600 font-medium">-</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {/* Cause de fin de contrat */}
                        <td className="p-3 text-center">
                          {!trial.active ? (
                            <div className="flex flex-col items-center gap-1">
                              {trial.termination_reason ? (
                                <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded font-medium">
                                  {trial.termination_reason}
                                </span>
                              ) : (
                                <select
                                  value=""
                                  onChange={(e) => handleUpdateTerminationReason(trial.user_id, e.target.value)}
                                  disabled={isSaving}
                                  className="text-xs px-2 py-1 border border-red-300 rounded bg-red-50 text-red-700 cursor-pointer"
                                  title="Sélectionner une cause (obligatoire)"
                                >
                                  <option value="">⚠️ Cause requise</option>
                                  {TERMINATION_REASONS.map(reason => (
                                    <option key={reason} value={reason}>{reason}</option>
                                  ))}
                                </select>
                              )}
                              {trial.deactivation_date && (
                                <span className="text-xs text-gray-500">
                                  {formatDate(trial.deactivation_date)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleSave(trial.user_id, trial.has_trial)}
                                disabled={isSaving}
                                className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                                title="Enregistrer"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                title="Annuler"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEdit(trial)}
                              disabled={trial.contract_type === 'cdi'}
                              className={`p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded ${
                                trial.contract_type === 'cdi' ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={trial.contract_type === 'cdi' ? 'CDI confirmé - non modifiable' : 'Modifier'}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredAndSortedTrials.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-gray-500">
                        Aucun salarié trouvé pour ce filtre
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Termination Reason Modal */}
        {showTerminationModal && terminationTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <FileX className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Désactiver le compte</h3>
                  <p className="text-sm text-gray-500">{terminationTarget.userName}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Veuillez sélectionner la cause de fin de contrat. Cette information est obligatoire.
              </p>
              
              <div className="space-y-2 mb-6">
                {TERMINATION_REASONS.map(reason => (
                  <label 
                    key={reason}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedReason === reason 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="termination_reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="w-4 h-4 text-red-600"
                    />
                    <span className={`text-sm ${selectedReason === reason ? 'font-medium text-red-700' : 'text-gray-700'}`}>
                      {reason}
                    </span>
                  </label>
                ))}
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowTerminationModal(false);
                    setTerminationTarget(null);
                    setSelectedReason('');
                  }}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleConfirmDeactivation}
                  disabled={!selectedReason || saving[terminationTarget?.userId]}
                >
                  {saving[terminationTarget?.userId] ? 'En cours...' : 'Confirmer la désactivation'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTrialPeriods;
